import { useState } from "react";
import { Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Side = ({ image, zones, placements, options, onToggle, art, fileUrl, compact, label, tint, big = false }) => {
  const names = options ? new Set(options.map((o) => o.name)) : null;
  const clickable = typeof onToggle === "function";
  const src = (n) => { const a = (art || {})[n]; return a?.image_url ? (fileUrl ? fileUrl(a.image_url) : a.image_url) : null; };
  const url = fileUrl ? fileUrl(image) : image;
  const imgCls = `w-full h-auto rounded-lg ${big ? "max-h-[78vh]" : compact ? "max-h-52" : "max-h-72"} object-contain`;
  return (
    <div className="relative w-full select-none min-w-0" data-testid={`mockup-side-${label.toLowerCase()}`}>
      {tint ? (
        <div className="relative" data-testid={`mockup-tint-${label.toLowerCase()}`}>
          <img src={url} alt={label} className={`${imgCls} invisible`} draggable={false} />
          <div className="absolute inset-0" style={{ backgroundColor: tint, WebkitMaskImage: `url(${url})`, maskImage: `url(${url})`, WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat", WebkitMaskPosition: "center", maskPosition: "center" }} />
          <img src={url} alt="" className={`${imgCls} absolute inset-0 mix-blend-multiply`} draggable={false} />
        </div>
      ) : <img src={url} alt={label} className={imgCls} draggable={false} />}
      {(zones || []).filter((z) => !names || names.has(z.name)).map((z) => {
        const on = placements.includes(z.name);
        const img = on ? src(z.name) : null;
        return (
          <div key={z.name} onClick={() => clickable && onToggle(z.name)} data-testid={`mockup-zone-${z.name}`} title={z.name}
            className={`absolute overflow-hidden rounded-[3px] ${clickable ? "cursor-pointer" : ""} ${on ? "border-[1.5px] border-[#ff6a13]" : compact ? "" : "border border-dashed border-zinc-400/70"} ${on && !img ? "bg-[#ff6a13]/60" : ""}`}
            style={{ left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%` }}>
            {img && <img src={img} alt="" className="w-full h-full object-contain" draggable={false} />}
          </div>
        );
      })}
      {!compact && <p className="text-[9px] font-mono-p text-zinc-600 text-center mt-1 uppercase tracking-wider">{label}</p>}
    </div>
  );
};

export function ImageMockup({ mockup, placements = [], options, onToggle, art, fileUrl, compact = false, view = "both", tint = null, enlargeable = true }) {
  const [big, setBig] = useState(false);
  const useTint = mockup.tintable ? (tint || mockup.tint_hex || null) : null;
  const hasBack = !!mockup.back_image_url;
  const backNames = new Set((mockup.back_zones || []).map((z) => z.name));
  const backActive = placements.some((p) => backNames.has(p));
  const frontActive = placements.some((p) => (mockup.zones || []).some((z) => z.name === p));
  const showFront = hasBack ? (compact ? (frontActive || !backActive) : view !== "back") : true;
  const showBack = hasBack && (compact ? (backActive && !frontActive) : view !== "front");
  const common = { placements, options, onToggle, art, fileUrl, compact, tint: useTint };
  const bigCommon = { placements, options, art, fileUrl, compact: true, big: true, tint: useTint };
  return (
    <div className={`relative ${compact ? "" : "rounded-xl border border-zinc-800 bg-[#0e1015] p-3 mb-3"}`} data-testid="image-mockup">
      <div className={`flex gap-3 ${showFront && showBack ? "" : ""}`}>
        {showFront && <Side image={mockup.image_url} zones={mockup.zones} label="Front" {...common} />}
        {showBack && <Side image={mockup.back_image_url} zones={mockup.back_zones} label="Back" {...common} />}
      </div>
      {!compact && typeof onToggle === "function" && <p className="text-[10px] text-zinc-600 text-center mt-1">Live preview · tap an area to add or remove a placement</p>}
      {enlargeable && (
        <button type="button" onClick={(e) => { e.stopPropagation(); setBig(true); }} aria-label="Enlarge preview" title="Enlarge preview" data-testid="mockup-enlarge"
          className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-black/70 hover:bg-[#ff6a13] text-white text-[10px] font-semibold px-2 py-1 border border-white/15 transition-colors">
          <Maximize2 size={11} />{!compact && "Enlarge"}
        </button>
      )}
      {enlargeable && (
        <Dialog open={big} onOpenChange={setBig}>
          <DialogContent className="bg-[#0f1116] border-zinc-800 text-white max-w-5xl w-[96vw] max-h-[92vh] overflow-y-auto p-4" data-testid="mockup-enlarged">
            <DialogHeader><DialogTitle className="font-display text-base">{mockup.name || "Garment preview"}</DialogTitle></DialogHeader>
            <div className={`grid gap-4 ${hasBack ? "sm:grid-cols-2" : "grid-cols-1"} bg-[#f1f1f3] rounded-xl p-4`}>
              <div><Side image={mockup.image_url} zones={mockup.zones} label="Front" {...bigCommon} /><p className="text-[10px] font-mono-p uppercase tracking-wider text-zinc-600 text-center mt-2">Front</p></div>
              {hasBack && <div><Side image={mockup.back_image_url} zones={mockup.back_zones} label="Back" {...bigCommon} /><p className="text-[10px] font-mono-p uppercase tracking-wider text-zinc-600 text-center mt-2">Back</p></div>}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
